import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { ApiResponse } from '../../models/api-response.model';
import { CompanyWithPayslips, CixorPaymentPayslipSummary, CixorPaymentPayslipDetail, InvoicePaymentLedgerItem } from '../../models/company-with-payslips.model';
import { PayrollCompany, PayrollPaymentSummary, PayrollPaymentDetail, ApprovePayrollPaymentRequest, RejectPayrollPaymentRequest } from '../../../features/manage-payment/payroll-payments/payroll-payment.model';

export type PayslipCompanyStatus = 'Approved' | 'Rejected' | 'Pending';

@Injectable({ providedIn: 'root' })
export class TransactionsService {
  private readonly baseUrl = `${environment.apiGatewayBaseUrl}/Transactions`;
  private readonly slipDownloadUrl = `${environment.apiGatewayBaseUrl}/Companies/payroll-invoice-report`;

  constructor(private http: HttpClient) {}

  getCompaniesWithPayslips(status: PayslipCompanyStatus): Observable<ApiResponse<CompanyWithPayslips[]>> {
    const params = new HttpParams().set('status', status);
    const url = `${this.baseUrl}/companies-with-payslips`;
    return this.http.get<ApiResponse<CompanyWithPayslips[]>>(url, { params });
  }

  getCompanyPayslipSummary(companyId: number | string, status: PayslipCompanyStatus): Observable<ApiResponse<CixorPaymentPayslipSummary[]>> {
    const params = new HttpParams().set('status', status);
    const url = `${this.baseUrl}/cixor-payment-payslip-summary/companyId/${companyId}`;
    return this.http.get<ApiResponse<CixorPaymentPayslipSummary[]>>(url, { params });
  }

  getCompanyPayslipDetails(companyId: number | string, paySlipId: number | string, status?: string): Observable<ApiResponse<CixorPaymentPayslipDetail[]>> {
    const url = `${this.baseUrl}/cixor-payment-payslip/company/${companyId}/slipId/${paySlipId}/status/${status ?? ''}`;
    return this.http.get<ApiResponse<CixorPaymentPayslipDetail[]>>(url);
  }

  getPendingPaymentsForPayslip(companyId: number, payslipId: number): Observable<ApiResponse<InvoicePaymentLedgerItem[]>> {
    const url = `${this.baseUrl}/cixor-payment-payslip/company/${companyId}/slipId/${payslipId}/pending-payments`;
    return this.http.get<ApiResponse<InvoicePaymentLedgerItem[]>>(url);
  }

  approveCixorPaymentPayslip(
    companyId: number,
    payslipId: number,
    approvedBy: number,
    actualPaymentDate: string,
    paymentIds?: number[]
  ): Observable<ApiResponse<any>> {
    const url = `${this.baseUrl}/approve-cixor-payment-payslip`;
    const body: any = { companyId, payslipId, approvedBy, actualPaymentDate };
    if (paymentIds && paymentIds.length > 0) {
      body.paymentIds = paymentIds;
    }
    return this.http.post<ApiResponse<any>>(url, body);
  }

  rejectCixorPaymentPayslip(companyId: number, payslipId: number, rejectionReason: string, rejectedBy: number): Observable<ApiResponse<any>> {
    const url = `${this.baseUrl}/reject-cixor-payment-payslip`;
    const body = {
      companyId,
      payslipId,
      rejectionReason,
      rejectedBy
    };
    return this.http.post<ApiResponse<any>>(url, body);
  }

  // ── Payroll Payments ──────────────────────────────────────────────

  getCompaniesWithPayrollInvoice(
    statusId?: number
  ): Observable<ApiResponse<PayrollCompany[]>> {
    let params = new HttpParams();
    if (statusId !== undefined) params = params.set('statusId', statusId);
    return this.http.get<ApiResponse<PayrollCompany[]>>(
      `${this.baseUrl}/companies-with-payroll-invoice`,
      { params }
    );
  }

  getPayrollPaymentSummary(
    companyId: number,
    statusId?: number
  ): Observable<ApiResponse<PayrollPaymentSummary[]>> {
    let params = new HttpParams();
    if (statusId !== undefined) params = params.set('statusId', statusId);
    return this.http.get<ApiResponse<PayrollPaymentSummary[]>>(
      `${this.baseUrl}/payroll-payment-invoice/companyId/${companyId}`,
      { params }
    );
  }

  getPayrollPaymentDetail(
    companyId: number,
    hdrId: number,
    statusId?: number
  ): Observable<ApiResponse<PayrollPaymentDetail[]>> {
    const sid = statusId !== undefined ? `/${statusId}` : '';
    return this.http.get<ApiResponse<PayrollPaymentDetail[]>>(
      `${this.baseUrl}/payroll-payment-invoice/company/${companyId}/hdrId/${hdrId}/statusId${sid}`
    );
  }

  approvePayrollPayment(
    body: ApprovePayrollPaymentRequest
  ): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${this.baseUrl}/approve-payroll-payment-invoice`,
      body
    );
  }

  rejectPayrollPayment(
    body: RejectPayrollPaymentRequest
  ): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${this.baseUrl}/reject-payroll-payment-invoice`,
      body
    );
  }

  downlaodPayrollPaymentPayslip(paySlipUrl: string): Observable<Blob> {
    const param = new HttpParams().set('invoiceReportBlobUrl', paySlipUrl);
    return this.http.get(
      `${this.slipDownloadUrl}/download`,
      { params: param, responseType: 'blob' }
    );
  }
}

